<template>
    <div class="mx-auto max-w-[1280px] px-3 py-4 sm:px-6 sm:pb-8 sm:pt-10">
        <!-- Header -->
        <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex min-w-0 items-center gap-2">
                <h1 class="min-w-0 break-words text-2xl font-medium text-new-neutral-800">
                    {{ apiToken.name }}
                </h1>
                <Button
                    variant="ghost"
                    size="icon"
                    class="size-8 shrink-0"
                    @click="showEditTokenModal"
                >
                    <Pencil class="size-4 text-new-neutral-400" />
                </Button>
            </div>
            <Button size="lg" class="self-start sm:self-auto" @click="showInstallationCode">
                <Code class="size-4" />
                Show Installation Code
            </Button>
        </div>

        <!-- Tabs -->
        <nav
            class="mb-6 flex items-center gap-1 overflow-x-auto border-b border-border [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Token sections"
        >
            <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                class="flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm whitespace-nowrap transition-colors"
                :class="
                    activeTab === tab.key
                        ? 'border-new-topol-blue bg-surface-card text-foreground dark:bg-muted'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                "
                @click="navigate(tab)"
            >
                {{ tab.label }}
                <WarningAltFilledIcon
                    v-if="tab.key === 'configuration' && hasNoDomains"
                    class="size-4 text-warning"
                />
            </button>
        </nav>

        <!-- Usage tab -->
        <Deferred v-if="activeTab === 'usage'" data="usage">
            <template #fallback>
                <div class="border border-border bg-white p-4 dark:bg-card">
                    <div class="flex w-full flex-wrap gap-2">
                        <div
                            v-for="n in 2"
                            :key="n"
                            class="h-[104px] w-full animate-pulse bg-surface-card dark:bg-muted sm:w-[250px]"
                        />
                    </div>
                    <div
                        class="mt-2 h-[400px] w-full animate-pulse bg-surface-card dark:bg-muted"
                    />
                </div>
            </template>

            <UsageTab
                :usage="usage"
                :api-token-id="apiToken.id"
                :timeframe="timeframe"
                :timeframe-options="timeframeOptions"
                :custom-range="customRange"
            />
        </Deferred>

        <!-- API Keys tab -->
        <ApiKeysCard v-else-if="activeTab === 'api-keys'" :api-token="apiToken" />

        <!-- Configuration tab -->
        <div v-else class="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <AllowedDomainsCard :domains="apiToken.domains" :token="apiToken" />
            <CustomStorageCard
                :api-token="apiToken"
                @select-provider="showStorageProviderModal"
            />
            <OpenAICard
                :has-key="!!props.apiToken.settings?.open_ai_api_key"
                :disabled="!props.isPluginSubscribed"
                @open-modal="openAIModal = true"
            />
            <DangerZoneCard @delete="showRemoveTokenModal(apiToken.id)" />
        </div>

        <!-- Dialogs -->
        <StorageDialog
            v-if="storageModal && storageProvider"
            :open="storageModal"
            :provider="storageProvider"
            :has-storage-settings-enabled="hasStorageSettingsEnabled"
            :token="apiToken"
            @close="closeStorageModal"
            @deleteModal="deleteStorageModalShow"
        />

        <DeleteStorageDialog
            v-if="deleteStorageModal && storageProvider"
            :open="deleteStorageModal"
            :provider="storageProvider"
            :token="apiToken"
            @close="closeDeleteStorageModal"
            @deleteStorage="deleteStorage"
        />

        <DeleteTokenDialog
            :open="removeTokenModal"
            :token="apiToken"
            :loading="removeTokenLoading"
            @remove="removeToken"
            @close="removeTokenModal = false"
        />

        <EditTokenDialog
            :open="editTokenModal"
            :token="apiToken"
            @save="updateTokenName"
            @close="editTokenModal = false"
        />

        <OpenAIDialog
            :open="openAIModal"
            :current-key="props.apiToken.settings?.open_ai_api_key || ''"
            @save="saveOpenAISettings"
            @delete="removeOpenAIKey"
            @close="openAIModal = false"
        />

        <InstallationCodeDialog
            :open="installationCodeModal"
            :api-token="apiToken"
            @close="installationCodeModal = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { PropType, ref, onMounted, computed } from 'vue';
import { Deferred, router, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import IToken from '@/types/Plugin/IToken';
import type UsagePayload from '@/types/Plugin/IUsage';
import type { IUsageTimeframeOption } from '@/types/Plugin/IUsage';
import { Button } from '@/components/ui/button';
import UsageTab from './components/UsageTab.vue';
import ApiKeysCard from './components/ApiKeysCard.vue';
import AllowedDomainsCard from './components/AllowedDomainsCard.vue';
import CustomStorageCard from './components/CustomStorageCard.vue';
import OpenAICard from './components/OpenAICard.vue';
import DangerZoneCard from './components/DangerZoneCard.vue';
import StorageDialog from './components/StorageDialog.vue';
import DeleteStorageDialog from './components/DeleteStorageDialog.vue';
import DeleteTokenDialog from './components/DeleteTokenDialog.vue';
import EditTokenDialog from './components/EditTokenDialog.vue';
import InstallationCodeDialog from './components/InstallationCodeDialog.vue';
import OpenAIDialog from './components/OpenAIDialog.vue';
import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue';
import WarningAltFilledIcon from './components/icons/WarningAltFilledIcon.vue';
import { Code, Pencil } from 'lucide-vue-next';

defineOptions({
    layout: DefaultLayoutV2,
});

type TabKey = 'usage' | 'api-keys' | 'configuration';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
    activeTab: {
        type: String as PropType<TabKey>,
        default: 'usage',
    },
    usage: {
        type: Object as PropType<UsagePayload | null>,
        default: null,
    },
    timeframe: {
        type: String,
        default: 'this-month',
    },
    timeframeOptions: {
        type: Array as PropType<IUsageTimeframeOption[]>,
        default: () => [],
    },
    customRange: {
        type: Object as PropType<{ from: string | null; to: string | null }>,
        default: () => ({ from: null, to: null }),
    },
    hasStorageSettingsEnabled: {
        type: Boolean,
        default: false,
    },
    isPluginSubscribed: {
        type: Boolean,
        default: false,
    },
});

const tabs: Array<{ key: TabKey; label: string; routeName: string }> = [
    { key: 'usage', label: 'Usage', routeName: 'plugin.api-tokens.usage' },
    { key: 'api-keys', label: 'API Keys', routeName: 'plugin.api-tokens.api-keys' },
    { key: 'configuration', label: 'Configuration', routeName: 'plugin.api-tokens.configuration' },
];

const navigate = (tab: { key: TabKey; routeName: string }) => {
    if (tab.key === props.activeTab) return;
    router.get(
        route(tab.routeName, { apiToken: props.apiToken.id }),
        {},
        { preserveScroll: true },
    );
};

// Warn on the Configuration tab while the token has no allowed domains.
const hasNoDomains = computed(() => {
    const domains = props.apiToken.domains;
    if (!domains) return true;
    return domains
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean).length === 0;
});

const storageProvider = ref<
    'aws' | 'gcs' | 'cloudflare' | 'digitalocean' | null
>(null);
const storageModal = ref(false);
const deleteStorageModal = ref(false);
const removeTokenModal = ref(false);
const removeTokenLoading = ref(false);
const editTokenModal = ref(false);
const installationCodeModal = ref(false);
const openAIModal = ref(false);

// After creating the team's very first token ever, the backend flashes
// `show_installation_code` so we open the installation dialog automatically.
onMounted(() => {
    const flash = usePage().props.flash as { data?: { show_installation_code?: boolean } } | undefined;
    if (flash?.data?.show_installation_code) {
        installationCodeModal.value = true;
    }
});

const showStorageProviderModal = (
    provider: 'aws' | 'cloudflare' | 'gcs' | 'digitalocean'
) => {
    storageProvider.value = provider;
    storageModal.value = true;
};

const closeStorageModal = () => {
    storageProvider.value = null;
    storageModal.value = false;
};

const deleteStorageModalShow = () => {
    storageModal.value = false;
    deleteStorageModal.value = true;
};

const closeDeleteStorageModal = () => {
    storageProvider.value = null;
    deleteStorageModal.value = false;
};

const deleteStorage = async (id: string) => {
    try {
        const res = await axios.delete(
            route('api-tokens.storage.delete', {
                apiToken: id,
            })
        );
        if (res.status === 204) {
            toast.success('Custom storage detached successfully!');
            router.reload({ only: ['apiToken'] });
            storageProvider.value = null;
            deleteStorageModal.value = false;
        }
    } catch (err) {
        toast.error('Failed to detach storage');
    }
};

const saveOpenAISettings = async (key: string) => {
    try {
        await axios.post(
            route('api-tokens.open-ai-settings.store', {
                apiToken: props.apiToken.id,
            }),
            { key }
        );
        toast.success('OpenAI settings saved successfully!');
        openAIModal.value = false;
        router.reload({ only: ['apiToken'] });
    } catch (e: any) {
        const message = e.response?.data?.message || 'Failed to save OpenAI settings';
        toast.error(message);
    }
};

const removeOpenAIKey = async () => {
    try {
        const res = await axios.delete(
            route('api-tokens.open-ai-settings.delete', {
                apiToken: props.apiToken.id,
            })
        );
        if (res.status === 204) {
            toast.success('OpenAI token removed successfully!');
            openAIModal.value = false;
            router.reload({ only: ['apiToken'] });
        }
    } catch (e) {
        toast.error('Failed to remove OpenAI token');
    }
};

const showRemoveTokenModal = (id: string) => {
    removeTokenModal.value = true;
};

const removeToken = async () => {
    removeTokenLoading.value = true;

    try {
        const res = await axios.delete(
            route('plugin.api-tokens.delete', {
                apiToken: props.apiToken.id,
            })
        );
        if (res.status === 204) {
            toast.success('API Token removed successfully!');
            router.visit(route('plugin.api-tokens.index'));
        }
    } catch (err) {
        toast.error('Failed to remove token');
    } finally {
        removeTokenLoading.value = false;
    }
};

const showEditTokenModal = () => {
    editTokenModal.value = true;
};

const updateTokenName = async (newName: string) => {
    try {
        const res = await axios.put(
            route('plugin.api-tokens.update', { apiToken: props.apiToken.id }),
            {
                name: newName,
            }
        );
        if (res.status === 200) {
            toast.success('Token name updated successfully!');
            router.reload({ only: ['apiToken'] });
            editTokenModal.value = false;
        }
    } catch (err) {
        toast.error('Failed to update token name');
    }
};

const showInstallationCode = () => {
    installationCodeModal.value = true;
};
</script>
